import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiService } from '../services/ApiService';
import { cacheService } from '../services/CacheService';
import '../styles/Dashboard.css';
import '../styles/theme.css';
import { FiUser, FiMapPin, FiCreditCard, FiCalendar, FiAlertTriangle } from 'react-icons/fi';
import { FormModal } from './ModalAlert';
import Header from './Header';
// Importar os geradores de PDF e Word
import { gerarPDF } from '../utils/PdfGenerator';
import { gerarDocumentoWord } from '../utils/WordGenerator';
import useConfirmationModal from '../hooks/useConfirmationModal';
import ConfirmationModal from './common/ConfirmationModal';

const Dashboard = () => {
  const { currentUser } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const { cliente: clienteFromUrl } = useParams();
  const { darkMode, toggleTheme } = useTheme();
  
  // Estados essenciais
  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  const [projectItems, setProjectItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Adicionar modais para gerenciamento de seções e itens
  const [addSectionModalOpen, setAddSectionModalOpen] = useState(false);
  const [sectionTitle, setSectionTitle] = useState('');
  const [isEditingSection, setIsEditingSection] = useState(false);
  const [editingSectionId, setEditingSectionId] = useState(null);

  // Estados para modal de item
  const [addItemModalOpen, setAddItemModalOpen] = useState(false);
  const [isEditingItem, setIsEditingItem] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [newItemData, setNewItemData] = useState({
    descricao: '',
    unidade: '',
    quantidade: 1,
    materialUnitario: 0,
    valorCusto: 0,
    fatorLucro: 1.7,
    moUnitario: 0,
    observacao: '',
    secaoId: ''
  });
  
  // Adicionar estado para rastrear mudanças não salvas
  const [saveStatus, setSaveStatus] = useState('initial');

  // Estado para o dropdown do usuário
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  // Adicionar estado para configurações
  const [configData, setConfigData] = useState({
    descricaoPadrao: '',
    maoDeObraUnitario: 0,
    maoDeObraCusto: 0,
    fatorMultiplicacao: 1.7
  });

  // Adicionar novo estado para configurações
  const [configuracoes, setConfiguracoes] = useState([]);
  const [selectedConfig, setSelectedConfig] = useState(null);

  // Adicionar estados para controlar a modal de PDF
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfEmpresaInfo, setPdfEmpresaInfo] = useState({
    nome: 'KMF ENGENHARIA',
    cnpj: '00.000.000/0001-00'
  });

  // Adicionar estados para controlar a exibição de modais de confirmação ao excluir seções e itens
  const [confirmDeleteSection, setConfirmDeleteSection] = useState(false);
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteCount, setDeleteCount] = useState(0);

  // Adicionar estado para controlar o timer de auto-salvamento
  const [autoSaveTimer, setAutoSaveTimer] = useState(null);

  const { 
    isModalOpen, 
    modalConfig, 
    showConfirmationModal, 
    hideConfirmationModal 
  } = useConfirmationModal();

  // Adicionar estado para controlar a exibição de modal de saída sem salvar
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [navigationAction, setNavigationAction] = useState(null);

  // Estado para controlar a navegação pendente
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Lista de unidades comuns para o dropdown
  const unidadesComuns = [
    'm²', 'm', 'm³', 'un', 'kg', 'l', 'h', 'dia', 'mês', 'ano',
    'cm', 'cm²', 'cm³', 'km', 'ha', 'ml', 'g', 'ton', 'cx', 'pç',
    'par', 'jogo', 'kit', 'rolo', 'lata', 'pct', 'saca', 'palete'
  ];

  // Adicionar novo estado para controlar o dropdown de unidades
  const [showUnidadesDropdown, setShowUnidadesDropdown] = useState(false);
  const [filteredUnidades, setFilteredUnidades] = useState(unidadesComuns);

  // Adicionar função para filtrar unidades
  const handleUnidadeFilter = (value) => {
    const filtered = unidadesComuns.filter(unidade => 
      unidade.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredUnidades(filtered);
    setNewItemData(prev => ({ ...prev, unidade: value }));
  };

  // Adicionar função para selecionar unidade
  const handleSelectUnidade = (unidade) => {
    setNewItemData(prev => ({ ...prev, unidade }));
    setShowUnidadesDropdown(false);
  };

  // Função para lidar com navegação quando há alterações não salvas
  const handleNavigationWithUnsavedChanges = (path) => {
    if (saveStatus === 'unsaved') {
      setNavigationAction(() => navigate(path));
      setShowUnsavedChangesModal(true);
      return false;
    }
    return true;
  };

  // Efeito para lidar com a navegação pendente
  useEffect(() => {
    if (pendingNavigation) {
      if (saveStatus === 'unsaved') {
        setNavigationAction(() => navigate('/orcamentos'));
        setShowUnsavedChangesModal(true);
      } else {
        navigate('/orcamentos');
      }
      setPendingNavigation(null);
    }
  }, [pendingNavigation, saveStatus, navigate]);

  // Função para navegar de volta para a lista de orçamentos
  const handleBack = () => {
    if (saveStatus === 'unsaved') {
      console.log(saveStatus); // Debug

      // Se houver alterações não salvas, mostrar o modal de confirmação
      setShowUnsavedChangesModal(true);
    } else {
      console.log(saveStatus); // Debug
      // Se não houver alterações, navegar diretamente
      navigate('/orcamentos');
    }
  };

  // Função para salvar e depois navegar
  const handleSaveAndNavigate = async () => {
    try {
      await handleSaveData();
      // A navegação será feita automaticamente após o salvamento bem-sucedido
      // através do processNavigation que é chamado após o salvamento
    } catch (error) {
      console.error('Erro ao salvar antes de navegar:', error);
      showNotification('error', 'Erro ao salvar alterações');
    }
  };

  // Processar a ação de navegação após a confirmação do usuário
  const processNavigation = useCallback(() => {
    if (navigationAction) {
      if (typeof navigationAction === 'function') {
        navigationAction();
      } else if (typeof navigationAction === 'string') {
        navigate(navigationAction);
      }
      setNavigationAction(null);
    }
    setShowUnsavedChangesModal(false);
  }, [navigationAction, navigate]);

  // Função para selecionar um projeto específico
  const handleSelectProject = (projectId) => {
    try {
      // Encontrar o projeto pelo ID
      const project = projects.find(p => p.id === projectId);
      
      if (project) {
        setActiveProject(project);
        loadProjectItems(project.id);
        
        // Atualizar a URL para o projeto selecionado
        const clienteNormalizado = project.cliente
          ? removerCaracteresEspeciais(project.cliente)
          : 'cliente';
        navigate(`/orcamento/${clienteNormalizado}`, { replace: true });
      } else {
        // Se o projeto não for encontrado, mostrar mensagem
        showNotification('error', 'Projeto não encontrado');
        
        // Se houver outros projetos disponíveis, selecionar o primeiro
        if (projects.length > 0) {
          const defaultProject = projects[0];
          setActiveProject(defaultProject);
          loadProjectItems(defaultProject.id);
          
          // Atualizar a URL para o projeto padrão
          const clienteNormalizado = defaultProject.cliente
            ? removerCaracteresEspeciais(defaultProject.cliente)
            : 'cliente';
          navigate(`/orcamento/${clienteNormalizado}`, { replace: true });
        }
      }
    } catch (error) {
      console.error('Erro ao selecionar projeto:', error);
      showNotification('error', 'Erro ao selecionar projeto');
    }
  };

  // Função para logout
  const handleLogout = () => {
    if (handleNavigationWithUnsavedChanges('/login')) {
      navigate('/login');
    }
  };
  
  // Carregar projetos e selecionar o primeiro
  useEffect(() => {
    loadProjects();
  }, [clienteFromUrl]);

  // Função para remover acentos e caracteres especiais de forma consistente
  const removerCaracteresEspeciais = (texto) => {
    if (!texto) return '';
    
    // Substituir acentos por caracteres sem acento
    const comAcento = 'áàãâäéèêëíìîïóòõôöúùûüçÁÀÃÂÄÉÈÊËÍÌÎÏÓÒÕÖÔÚÙÛÜÇ';
    const semAcento = 'aaaaaeeeeiiiiooooouuuucAAAAAEEEEIIIIOOOOOUUUUC';
    
    let result = texto;
    for (let i = 0; i < comAcento.length; i++) {
      result = result.replace(new RegExp(comAcento[i], 'g'), semAcento[i]);
    }
    
    // Substituir espaços por traços e remover caracteres especiais
    return result
      .trim()
      .replace(/\s+/g, '-')        // Substituir espaços por traços
      .replace(/[^a-zA-Z0-9-]/g, '') // Manter apenas letras, números e traços
      .toLowerCase();              // Converter para minúsculas
  };
  
  // Função para carregar projetos
  const loadProjects = async () => {
    try {
      setLoading(true);
      const result = await apiService.getOrcamentos();
      if (result.success && result.data && Array.isArray(result.data.orcamentos)) {
        setProjects(result.data.orcamentos);
        
        // Se temos um cliente na URL, encontrar e selecionar esse projeto
        if (clienteFromUrl && result.data.orcamentos.length > 0) {
          // Encontrar projeto comparando com nome normalizado do cliente na URL
          const projectToSelect = result.data.orcamentos.find(
            p => p && p.cliente && removerCaracteresEspeciais(p.cliente) === clienteFromUrl
          );
          
          if (projectToSelect) {
            setActiveProject(projectToSelect);
            loadProjectItems(projectToSelect.id);
          } else {
            // Se não encontrar o projeto da URL, selecionar o primeiro
            const firstProject = result.data.orcamentos[0];
            setActiveProject(firstProject);
            loadProjectItems(firstProject.id);
            // Atualizar a URL para o primeiro projeto
            const clienteNormalizado = firstProject.cliente
              ? removerCaracteresEspeciais(firstProject.cliente)
              : 'cliente';
            navigate(`/orcamento/${clienteNormalizado}`, { replace: true });
          }
        } else if (result.data.orcamentos.length > 0) {
          // Se não tiver cliente na URL, selecionar o primeiro projeto
          const firstProject = result.data.orcamentos[0];
          setActiveProject(firstProject);
          loadProjectItems(firstProject.id);
          
          // Atualizar a URL para o primeiro projeto
          const clienteNormalizado = firstProject.cliente
              ? removerCaracteresEspeciais(firstProject.cliente)
              : 'cliente';
          navigate(`/orcamento/${clienteNormalizado}`, { replace: true });
        }
      } else {
        console.error('Erro ao carregar projetos:', result.error || 'Erro desconhecido');
        showNotification('error', 'Não foi possível carregar os orçamentos');
        setProjects([]);
      }
    } catch (error) {
      console.error('Erro ao carregar projetos:', error);
      showNotification('error', 'Erro ao buscar dados de orçamentos');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Função para carregar itens de um projeto
  const loadProjectItems = async (projectId) => {
    try {
      if (!projectId) return;
      
      setLoading(true);
      const result = await apiService.getProjetoItens(projectId);
      
      if (result && result.success && result.data) {
        // Garantir que itens seja sempre um array
        setProjectItems(Array.isArray(result.data.itens) ? result.data.itens : []);
      } else {
        console.error('Erro ao carregar itens do projeto:', result?.error || 'Erro desconhecido');
        showNotification('error', 'Não foi possível carregar os itens do orçamento');
        // Garantir que projectItems seja sempre um array
          setProjectItems([]);
      }
    } catch (error) {
      console.error('Erro ao carregar itens do projeto:', error);
      showNotification('error', 'Erro ao buscar dados dos itens');
      // Garantir que projectItems seja sempre um array em caso de erro
      setProjectItems([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Função para formatar valor em moeda brasileira
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };
  
  // Separar itens por seção
  const getItemsBySections = () => {
    const sections = [];
    
    // Primeiro, encontrar todas as seções
    const secoes = projectItems.filter(item => item.tipo === 'secao');
    
    // Verificar se há seções disponíveis
    if (secoes.length === 0) {
      // Se não houver seções, colocar todos os itens em uma seção "Geral"
      const itensNaoSecao = projectItems.filter(item => item.tipo !== 'secao');
      
      if (itensNaoSecao.length > 0) {
        sections.push({
          id: 'geral',
          titulo: 'Itens Gerais',
          itens: itensNaoSecao
        });
      }
    } else {
      // Para cada seção, adicionar seus itens
      secoes.forEach(secao => {
        // Obter o título correto da seção
        const titulo = secao.descricao || secao.titulo || 'Seção sem título';
        const secaoId = String(secao.id);
        
        
        // Listar todos os itens, convertendo seus secaoId para garantir comparação consistente
        const itensDaSecao = projectItems.filter(item => {
          if (item.tipo === 'secao') return false;
          
          const itemSecaoId = item.secaoId ? String(item.secaoId) : '';
          const match = itemSecaoId === secaoId;
          
          return match;
        });
        
        sections.push({
          id: secao.id,
          titulo: titulo,
          itens: itensDaSecao
        });
      });
      
      // Adicionar uma seção especial para itens que não têm seção associada
      const itensSemSecao = projectItems.filter(item => {
        if (item.tipo === 'secao') return false;
        
        const itemSecaoId = item.secaoId ? String(item.secaoId) : '';
        
        // Verificar se a seção associada ainda existe
        const temSecaoValida = secoes.some(secao => String(secao.id) === itemSecaoId);
        
        return !itemSecaoId || !temSecaoValida;
      });
      
      if (itensSemSecao.length > 0) {
        sections.push({
          id: 'sem-secao',
          titulo: 'Itens sem Seção',
          itens: itensSemSecao
        });
      }
    }
    
    return sections;
  };

  // Funções para gerenciamento de seções
  const handleAddSection = () => {
    setAddSectionModalOpen(true);
    setIsEditingSection(false);
    setSectionTitle('');
  };

  const handleEditSection = (section) => {
    setAddSectionModalOpen(true);
    setIsEditingSection(true);
    setEditingSectionId(section.id);
    setSectionTitle(section.descricao);
  };

  const handleSaveSection = async () => {
    if (!sectionTitle.trim()) {
      showNotification('error', 'O título da seção é obrigatório');
      return;
    }
    
    try {
      setLoading(true);
      
      // Preparar os dados da seção
      const sectionData = {
        id: isEditingSection && editingSectionId ? editingSectionId : Date.now().toString(),
        descricao: sectionTitle,
        tipo: 'secao'
      };
      
      // Se estiver editando, atualizar seção existente
      if (isEditingSection && editingSectionId) {
        setProjectItems(prev => 
          prev.map(item => item.id === editingSectionId ? sectionData : item)
        );
      } else {
        // Adicionar nova seção
        setProjectItems(prev => [...prev, sectionData]);
      }
      
      // Fechar modal e limpar valores
      setAddSectionModalOpen(false);
      setSectionTitle('');
      setIsEditingSection(false);
      setEditingSectionId(null);
      
      // Marcar como não salvo
      markAsUnsaved();
    } catch (error) {
      console.error('Erro ao salvar seção:', error);
      showNotification('error', 'Erro ao salvar seção');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSection = async (section) => {
    if (!section || !section.id) {
      console.error('Seção inválida:', section);
      showNotification('error', 'Erro ao excluir seção: seção inválida');
      return;
    }
    
    // Contar quantos itens pertencem a esta seção
    const itensNaSecao = projectItems.filter(item => 
      item.tipo !== 'secao' && String(item.secaoId) === String(section.id)
    ).length;
    
    // Definir a seção a ser excluída e o número de itens relacionados
    setSectionToDelete(section);
    setDeleteCount(itensNaSecao);
    
    // Abrir a modal de confirmação
    setConfirmDeleteSection(true);
  };
  
  // Função que será chamada para confirmar a exclusão da seção
  const confirmSectionDeletion = async () => {
    try {
      setLoading(true);
      
      // 1. Remover a seção
      const updatedItems = projectItems.filter(item => item.id !== sectionToDelete.id);
      
      // 2. Remover todos os itens associados a esta seção
      const finalItems = updatedItems.filter(item => {
        // Se for uma seção ou não tiver secaoId, manter
        if (item.tipo === 'secao' || !item.secaoId) return true;
        
        // Se o secaoId for diferente da seção que estamos excluindo, manter
        return item.secaoId !== sectionToDelete.id;
      });
      
      // Atualizar o estado
      setProjectItems(finalItems);
      setSectionToDelete(null);
      setConfirmDeleteSection(false);
      
      // Mostrar notificação
      showNotification('success', 'Seção excluída com sucesso');
      
      // Marcar como não salvo
      markAsUnsaved();
    } catch (error) {
      console.error('Erro ao excluir seção:', error);
      showNotification('error', 'Erro ao excluir seção');
    } finally {
      setLoading(false);
    }
  };

  // Obter lista atualizada de seções disponíveis
  const getAvailableSections = useCallback(() => {
    return projectItems
      .filter(item => item.tipo === 'secao')
      .map(section => ({
        id: section.id,
        descricao: section.descricao || section.titulo || 'Seção sem título'
      }));
  }, [projectItems]);

  // Funções para gerenciamento de itens
  const handleAddItem = () => {
    // Obter todas as seções disponíveis com a função atualizada
    const sections = getAvailableSections();
    
    // Inicializar com valores padrão
    setIsEditingItem(false);
    setEditingItem(null);
    
    // Garantir que nenhuma configuração esteja pré-selecionada
    setSelectedConfig(null);
    
    // Definir os valores iniciais para o novo item
    const defaultSecaoId = sections.length > 0 ? sections[0].id : '';
    
    setNewItemData({
      descricao: '',
      unidade: '',
      quantidade: 1,
      materialUnitario: 0,
      moUnitario: 0,
      valorCusto: 0,
      fatorLucro: 1.7,
      observacao: '',
      secaoId: defaultSecaoId
    });
    
    // Abrir o modal
    setAddItemModalOpen(true);
  };

  const handleEditItem = (item) => {
    if (!item) {
      console.error('Item inválido:', item);
      showNotification('error', 'Erro ao editar item: item inválido');
      return;
    }
    
    // Obter todas as seções disponíveis usando a função atualizada
    const sections = getAvailableSections();
    
    // Verificar se a seção associada ainda existe
    const secaoExiste = !item.secaoId || sections.some(s => s.id === item.secaoId);
    
    // Se a seção não existir mais, usar a primeira seção disponível ou deixar vazio
    const secaoId = secaoExiste ? item.secaoId : (sections.length > 0 ? sections[0].id : '');
    
    // Calcular MO Unitário baseado no valor de custo e fator de lucro
    const valorCusto = parseFloat(item.valorCusto) || 0;
    const fatorLucro = parseFloat(item.fatorLucro) || 1.7;
    const moUnitario = Math.round((valorCusto * fatorLucro) * 100) / 100;
    
    // Preparar o estado para edição
    setIsEditingItem(true);
    setEditingItem(item);
    setNewItemData({
      descricao: item.descricao || '',
      unidade: item.unidade || '',
      quantidade: parseFloat(item.quantidade) || 1,
      materialUnitario: parseFloat(item.materialUnitario) || 0,
      valorCusto: valorCusto,
      fatorLucro: fatorLucro,
      moUnitario: moUnitario,
      observacao: item.observacao || '',
      secaoId: secaoId
    });
    
    // Garantir que nenhuma configuração esteja pré-selecionada
    setSelectedConfig(null);
    
    // Abrir o modal
    setAddItemModalOpen(true);
  };

  const handleItemInputChange = (e) => {
    const { name, value } = e.target;
    
    // Converter para número onde necessário
    if (['quantidade', 'materialUnitario', 'moUnitario'].includes(name)) {
      setNewItemData({
        ...newItemData,
        [name]: parseFloat(value) || 0
      });
        } else {
      setNewItemData({
        ...newItemData,
        [name]: value
      });
    }
  };

  const handleSaveItem = async () => {
    // Validações básicas
    if (!newItemData.descricao.trim()) {
      showNotification('error', 'Descrição é obrigatória');
      return;
    }
    
    if (!newItemData.unidade.trim()) {
      showNotification('error', 'Unidade é obrigatória');
      return;
    }
    
    if (!newItemData.quantidade || isNaN(parseFloat(newItemData.quantidade)) || parseFloat(newItemData.quantidade) <= 0) {
      showNotification('error', 'Quantidade deve ser maior que zero');
      return;
    }
    
    try {
      // Recalcular MO Unitário baseado no valor de custo e fator de lucro
      const valorCusto = newItemData.valorCusto || 0;
      const fatorLucro = newItemData.fatorLucro || 1.7;
      const moUnitario = Math.round((valorCusto * fatorLucro) * 100) / 100;
      
      // Calcular valores totais
      const materialUnitario = newItemData.materialUnitario || 0;
      const quantidade = newItemData.quantidade || 0;
      
      const materialTotal = Math.round((materialUnitario * quantidade) * 100) / 100;
      const moTotal = Math.round((moUnitario * quantidade) * 100) / 100;
      
      // Preparar os dados do item
      const itemData = {
        ...newItemData,
        id: isEditingItem && editingItem ? editingItem.id : Date.now().toString(),
        moUnitario: moUnitario,
        materialTotal,
        moTotal,
        tipo: 'item'
      };
      
      // Verificar se estamos editando ou criando
      if (isEditingItem && editingItem) {
        // Atualizar o item existente
        setProjectItems(prev => 
          prev.map(item => item.id === editingItem.id ? itemData : item)
        );
      } else {
        // Adicionar novo item
        setProjectItems(prev => [...prev, itemData]);
      }
      
      // Fechar o modal e limpar os dados
      setAddItemModalOpen(false);
      setIsEditingItem(false);
      setEditingItem(null);
      setNewItemData({
        descricao: '',
        unidade: '',
        quantidade: 1,
        materialUnitario: 0,
        valorCusto: 0,
        fatorLucro: 1.7,
        moUnitario: 0,
        observacao: '',
        secaoId: ''
      });
      
      // Marcar como não salvo
      markAsUnsaved();
      
      // Mostrar notificação de sucesso
      showNotification('success', isEditingItem ? 'Item atualizado com sucesso' : 'Item adicionado com sucesso');
    } catch (error) {
      console.error('Erro ao salvar item:', error);
      showNotification('error', 'Erro ao salvar item');
    }
  };

  const handleDeleteItem = async (item) => {
    if (!item) {
      console.error('Item inválido:', item);
      showNotification('error', 'Erro ao excluir item: item inválido');
      return;
    }
    
    // Definir o item a ser excluído
    setItemToDelete(item);
    
    // Abrir a modal de confirmação
    setConfirmDeleteItem(true);
  };
  
  // Função que será chamada para confirmar a exclusão do item
  const confirmItemDeletion = async () => {
    try {
      if (!itemToDelete) return;
      
      // Remover o item
      const updatedItems = projectItems.filter(item => item.id !== itemToDelete.id);
      
      // Atualizar o estado
      setProjectItems(updatedItems);
      setItemToDelete(null);
      setConfirmDeleteItem(false);
      
      // Mostrar notificação
      showNotification('success', 'Item excluído com sucesso');
      
      // Marcar como não salvo
      markAsUnsaved();
    } catch (error) {
      console.error('Erro ao excluir item:', error);
      showNotification('error', 'Erro ao excluir item');
    }
  };

  // Função para atualizar o saveStatus quando há alterações
  const markAsUnsaved = useCallback(() => {
    console.log('Marcando como não salvo'); // Debug
    setSaveStatus('unsaved');
  }, []);

  // Funções para cálculo de totais
  const calcularTotal = () => {
    return Math.round((projectItems.reduce((total, item) => {
      if (item.tipo !== 'secao') {
        const materialTotal = (item.materialUnitario || 0) * (item.quantidade || 0);
        const moTotal = (item.moUnitario || 0) * (item.quantidade || 0);
        const custoTotal = (item.valorCusto || 0) * (item.quantidade || 0);
        return total + materialTotal + moTotal + custoTotal;
      }
      return total;
    }, 0)) * 100) / 100;
  };

  const calcularTotalMaterial = () => {
    return Math.round((projectItems.reduce((total, item) => {
      if (item.tipo !== 'secao') {
        return total + ((item.materialUnitario || 0) * (item.quantidade || 0));
      }
      return total;
    }, 0)) * 100) / 100;
  };

  const calcularTotalMO = () => {
    return Math.round((projectItems.reduce((total, item) => {
      if (item.tipo !== 'secao') {
        return total + ((item.moUnitario || 0) * (item.quantidade || 0));
      }
      return total;
    }, 0)) * 100) / 100;
  };

  const calcularTotalCusto = () => {
    return Math.round((projectItems.reduce((total, item) => {
      if (item.tipo !== 'secao') {
        return total + ((item.valorCusto || 0) * (item.quantidade || 0));
      }
      return total;
    }, 0)) * 100) / 100;
  };

  const calcularTotalBdi = () => {
    const totalMaterial = calcularTotalMaterial();
    const totalMO = calcularTotalMO();
    const totalCusto = calcularTotalCusto();
    const bdi = activeProject?.bdi || 0;
    return Math.round((((totalMaterial + totalMO + totalCusto) * bdi) / 100) * 100) / 100;
  };

  const calcularTotalGeral = () => {
    const totalMaterial = calcularTotalMaterial();
    const totalMO = calcularTotalMO();
    const totalCusto = calcularTotalCusto();
    const totalBdi = calcularTotalBdi();
    return Math.round((totalMaterial + totalMO + totalCusto + totalBdi) * 100) / 100;
  };

  // Função para salvar todas as alterações
  const handleSaveData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Preparar os dados do projeto
      const projectData = {
        projeto: {
          ...activeProject,
          valorTotal: calcularTotal(),
          valorTotalMaterial: calcularTotalMaterial(),
          valorTotalMO: calcularTotalMO(),
          valorTotalCusto: calcularTotalCusto(),
          valorTotalBdi: calcularTotalBdi(),
          valorTotalGeral: calcularTotalGeral()
        },
        itens: projectItems || []
      };

      // Usar saveAllProjectData em vez de updateOrcamento
      const result = await apiService.saveAllProjectData(activeProject.id, projectData);
      
      if (result.success) {
        setActiveProject(result.data.projeto);
        setProjectItems(result.data.itens || []);
        console.log('Salvando alterações, atualizando status para saved'); // Debug
        setSaveStatus('saved'); // Atualizar para 'saved' quando salva com sucesso
        showNotification('success', 'Orçamento salvo com sucesso!');
        
        // Criar um timer para limpar o status após 3 segundos
        setTimeout(() => {
          console.log('Limpando status após 3 segundos'); // Debug
          setSaveStatus('initial');
        }, 3000);
        
        // Se o método foi chamado da modal de confirmação, não redirecionamos automaticamente
        if (!navigationAction) {
          // Navegar para a página de orçamentos após salvar com sucesso
          navigate('/orcamentos');
        }
      } else {
        setError(result.error || 'Erro ao salvar orçamento');
        showNotification('error', `Erro ao salvar orçamento: ${result.error}`);
      }
    } catch (error) {
      console.error('Erro ao salvar orçamento:', error);
      setError('Erro ao salvar orçamento');
      showNotification('error', 'Erro ao salvar orçamento');
    } finally {
      setLoading(false);
    }
  };

  // Função para auto-salvar sem mostrar indicadores de carregamento ou navegar
  const handleAutoSave = useCallback(async () => {
    if (!activeProject || !activeProject.id || loading) return;
    
    try {
      // Preparar os dados do projeto
      const projectData = {
        projeto: {
          ...activeProject,
          valorTotal: calcularTotal(),
          valorTotalMaterial: calcularTotalMaterial(),
          valorTotalMO: calcularTotalMO(),
          valorTotalCusto: calcularTotalCusto(),
          valorTotalBdi: calcularTotalBdi(),
          valorTotalGeral: calcularTotalGeral()
        },
        itens: projectItems || []
      };
      
      // Chamar a API para salvar
      await apiService.saveAllProjectData(activeProject.id, projectData);
      
      // Mostrar notificação de sucesso e atualizar o estado
      showNotification('success', 'Alterações salvas automaticamente');
      setSaveStatus('saved'); // Atualizar para 'saved' quando salva com sucesso
      
      // Criar um timer para limpar o status após 3 segundos
      setTimeout(() => {
        setSaveStatus('initial');
      }, 3000);
      
    } catch (error) {
      console.error('Erro durante o auto-salvamento:', error);
      showNotification('error', 'Erro ao salvar automaticamente. Tente salvar manualmente.');
    }
  }, [
    activeProject, 
    projectItems, 
    loading,
    calcularTotal,
    calcularTotalMaterial,
    calcularTotalMO,
    calcularTotalCusto,
    calcularTotalBdi,
    calcularTotalGeral,
    showNotification
  ]);

  // Efeito para recarregar as configurações quando houver mudança no usuário
  useEffect(() => {
    if (currentUser) {
      loadConfigData();
      loadConfiguracoes();
    }
  }, [currentUser]);

  // Efeito para carregar configurações e dados de configuração ao iniciar
  useEffect(() => {
    loadConfigData();
    loadConfiguracoes();
  }, []);

  // Função para carregar dados de configuração do localStorage
  const loadConfigData = () => {
    try {
      const savedConfig = localStorage.getItem('orcamentoConfig');
      if (savedConfig) {
        setConfigData(JSON.parse(savedConfig));
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      // Dados padrão em caso de erro
      setConfigData({
        descricaoPadrao: '',
        maoDeObraUnitario: 0,
        maoDeObraCusto: 0,
        fatorMultiplicacao: 1.7
      });
    }
  };

  // Função para carregar configurações do localStorage
  const loadConfiguracoes = () => {
    try {
      // Tentar primeiro carregar do localStorage com 'orcamentoConfigs' (usado no OrcamentosList)
      let savedConfigs = JSON.parse(localStorage.getItem('orcamentoConfigs')) || [];
      
      // Se não encontrar, tentar com 'configuracoes' (nome alternativo que pode estar sendo usado)
      if (!savedConfigs || savedConfigs.length === 0) {
        savedConfigs = JSON.parse(localStorage.getItem('configuracoes')) || [];
      } else {
        console.log(savedConfigs);
      }
      
      // Função para normalizar string de empresa (para comparações mais robustas)
      const normalizeEmpresa = (empresa) => {
        if (!empresa) return '';
        return empresa.toString().trim().toLowerCase();
      };

      // Função para verificar se a configuração pertence à empresa do usuário
      const isConfigFromUserCompany = (config) => {
        if (!config) return false;
        
        // Admin sempre vê todas as configurações
        if (currentUser?.role === 'admin') return true;
        
        const empresaUsuario = normalizeEmpresa(currentUser?.empresa);
        const empresaConfig = normalizeEmpresa(config.empresa);
        
        // Se a config não tem empresa definida, consideramos que é visível para todos
        if (!empresaConfig) return true;
        
        // Comparação exata entre as empresas normalizadas
        return empresaConfig === empresaUsuario;
      };
      
      // Filtrar configurações com base na empresa
      const configsFiltradas = savedConfigs.filter(isConfigFromUserCompany);
      
      setConfiguracoes(configsFiltradas);
      
      // Forçar salvamento em ambos os formatos para garantir compatibilidade
      localStorage.setItem('orcamentoConfigs', JSON.stringify(savedConfigs));
      localStorage.setItem('configuracoes', JSON.stringify(savedConfigs));
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      setConfiguracoes([]);
    }
  };

  // Função para aplicar valores padrão da configuração
  const handleApplyDefaultValues = () => {
    const fatorLucro = Math.round((configData.fatorMultiplicacao || 1.7) * 100) / 100;
    const valorCusto = newItemData.valorCusto || 0;
    const moUnitario = Math.round((valorCusto * fatorLucro) * 100) / 100;
    
    setNewItemData(prev => ({
      ...prev,
      fatorLucro: fatorLucro,
      moUnitario: moUnitario
    }));
  };

  // Modal para adicionar/editar item
  const handleCloseModal = () => {
    setAddItemModalOpen(false);
    setIsEditingItem(false);
    setEditingItem(null);
    setSelectedConfig(null);
    setNewItemData({
      descricao: '',
      unidade: '',
      quantidade: 1,
      materialUnitario: 0,
      valorCusto: 0,
      fatorLucro: 1.7,
      moUnitario: 0,
      observacao: '',
      secaoId: ''
    });
  };

  // Usar o hook useCallback para memorizar as seções disponíveis
  const availableSections = getAvailableSections();

  const handleConfigSelect = (config) => {
    if (!config) return;
    
    setSelectedConfig(config);
    
    // Calcular MO Unitário baseado no custo e fator de multiplicação
    const valorCusto = config.maoDeObraCusto || 0;
    const fatorLucro = config.fatorMultiplicacao || 1.7;
    const moUnitario = Math.round((valorCusto * fatorLucro) * 100) / 100;
    
    // Atualizar todos os campos com os valores da configuração
    const updatedItemData = {
      ...newItemData,
      descricao: config.descricao || newItemData.descricao || '',
      unidade: config.unidade || newItemData.unidade || '',
      materialUnitario: parseFloat(config.materialUnitario || newItemData.materialUnitario || 0),
      valorCusto: parseFloat(valorCusto || 0),
      fatorLucro: parseFloat(fatorLucro || 1.7),
      moUnitario: parseFloat(moUnitario || 0),
      observacao: config.observacao || newItemData.observacao || ''
      // Manter a quantidade e seção atual
    };
    
    setNewItemData(updatedItemData);
  };

  // Função para navegar para a página inicial
  const handleNavigateToHome = () => {
    if (handleNavigationWithUnsavedChanges('/home')) {
      navigate('/home');
    }
  };

  // Função para gerar PDF - atualizada para usar modal
  const handleGeneratePDF = async () => {
    try {
      // Buscar configurações da conta
      const resultado = await apiService.getDiarioObraConfiguracao();
      let configEmpresa = null;
      
      if (resultado.success && resultado.data) {
        configEmpresa = resultado.data;
      }
      
      // Abrir a modal para preencher as informações
      setPdfEmpresaInfo({
        nome: configEmpresa?.dadosConstrutora?.nome || currentUser?.empresa || 'KMF ENGENHARIA',
        cnpj: configEmpresa?.dadosConstrutora?.cnpj || '00.000.000/0001-00'
      });
      setPdfModalOpen(true);
    } catch (error) {
      console.error('Erro ao carregar configurações da empresa:', error);
      showNotification('error', 'Erro ao carregar configurações da empresa');
    }
  };
  
  // Função para processar os dados da modal e gerar o PDF
  const handleGeneratePDFWithInfo = async () => {
    try {
      // Verificar se existem itens no orçamento
      const itensOrcamento = projectItems.filter(item => item.tipo !== 'secao');
      if (!itensOrcamento || itensOrcamento.length === 0) {
        showNotification('error', 'Não é possível gerar o PDF. O orçamento não possui itens cadastrados.');
        setPdfModalOpen(false);
        return;
      }

      setPdfModalOpen(false);
      showNotification('info', 'Gerando PDF. O download começará em instantes...');
      
      // Calcular totais para incluir no PDF
      const totais = {
        valorTotalMaterial: calcularTotalMaterial(),
        valorTotalMO: calcularTotalMO(),
        valorTotalGeral: calcularTotalGeral()
      };
      
      // Criar objeto de projeto para o PDF com os totais calculados
      const projectWithTotals = {
        ...activeProject,
        ...totais
      };
      
      // Passar o nome da empresa e CNPJ para a função de geração de PDF
      await gerarPDF(projectWithTotals, projectItems, pdfEmpresaInfo.nome, pdfEmpresaInfo.cnpj);
      
      showNotification('success', 'PDF gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      showNotification('error', `Erro ao gerar PDF: ${error.message || 'Erro desconhecido'}`);
    }
  };

  // Função para gerar documento Word
  const handleGenerateWord = async () => {
    try {
      // Dados do formulário (pode ser personalizado conforme necessidade)
      const formData = {
        destinatario: activeProject.cliente || 'Cliente',
        empresa: activeProject.empresa || 'Empresa',
        responsavel: currentUser?.nome || 'Responsável',
        cargo: 'Engenheiro',
        telefone: currentUser?.telefone || '(00) 00000-0000',
        email: currentUser?.email || 'contato@kmfengenharia.com.br'
      };
      
      showNotification('info', 'Gerando documento Word. O download começará em instantes...');
      await gerarDocumentoWord(formData, activeProject, projectItems);
      showNotification('success', 'Documento Word gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar documento Word:', error);
      showNotification('error', `Erro ao gerar documento Word: ${error.message || 'Erro desconhecido'}`);
    }
  };

  // Efeito para mostrar confirmação ao tentar fechar a janela com alterações não salvas
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (saveStatus === 'unsaved') {
        // Mensagem padrão que será mostrada pelo navegador
        const message = 'Há alterações não salvas. Se sair, essas alterações serão perdidas. Deseja sair mesmo assim?';
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [saveStatus]);

  // Cancelar a navegação
  const cancelNavigation = useCallback(() => {
    setNavigationAction(null);
    setShowUnsavedChangesModal(false);
  }, []);

  // Atualizar outras funções de navegação
  const handleNavigateToOrcamentos = () => {
    if (handleNavigationWithUnsavedChanges('/orcamentos')) {
      navigate('/orcamentos');
    }
  };

  const handleNavigateToDiarioObra = () => {
    if (handleNavigationWithUnsavedChanges('/diario-obra')) {
      navigate('/diario-obra');
    }
  };

  // Modal de confirmação para saída sem salvar
  const renderUnsavedChangesModal = () => {
    if (!showUnsavedChangesModal) return null;

    return (
      <div className="modal-overlay">
        <div className="modal confirm-modal">
          <div className="modal-header warning">
            <h3><FiAlertTriangle /> Alterações não salvas</h3>
            <button className="modal-close" onClick={cancelNavigation}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="modal-body">
            <p><strong>Você tem alterações não salvas!</strong></p>
            <p>Se sair agora, todas as suas alterações serão perdidas.</p>
            <p>Deseja salvar as alterações antes de sair?</p>
          </div>
          <div className="modal-footer">
            <button className="cancel-button" onClick={cancelNavigation}>
              Cancelar
            </button>
            <button 
              className="secondary-button" 
              onClick={handleSaveAndNavigate}
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Salvando...
                </>
              ) : (
                'Salvar e sair'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`dashboard-container ${darkMode ? 'dark-mode' : ''}`}>
      <Header 
        onLogout={handleLogout}
        onNavigateToHome={handleNavigateToHome}
        onNavigateToOrcamentos={handleNavigateToOrcamentos}
        onNavigateToDiarioObra={handleNavigateToDiarioObra}
        onAction={(action, message) => {
          if (action === 'save') {
            handleSaveData();
          } else if (action === 'pdf') {
            handleGeneratePDF();
          } else if (action === 'word') {
            handleGenerateWord();
          }
        }}
        showActionButtons={true}
        isOffline={currentUser?.isOffline}
        saveStatus={saveStatus}
      />
      
      <div className="dashboard-main">
        <div className="content-area">
            {loading ? (
            <div className="loading-container">
              <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem' }}></i>
              <p>Carregando orçamento...</p>
                  </div>
          ) : (
            <div className="content-container">
              {activeProject ? (
            <div className="project-details">
              <button className="back-to-orcamentos" onClick={handleBack}>
                <i className="fas fa-arrow-left"></i>
                <span>Voltar para Orçamentos</span>
              </button>
              <div className="project-header">
                    <h2>{activeProject.name || 'Orçamento sem nome'}</h2>
                <div className="project-info">
                      <p>
                        <FiUser />
                        <strong>Cliente:</strong> {activeProject.cliente || 'Cliente não informado'}
                      </p>
                      <p>
                        <FiCalendar />
                        <strong>Status:</strong> {activeProject.status || 'Novo'}
                      </p>
                      {activeProject.enderecoObra && (
                        <p>
                          <FiMapPin />
                          <strong>Endereço:</strong> {activeProject.enderecoObra}
                        </p>
                      )}
                      {activeProject.formaPagamento && (
                        <p>
                          <FiCreditCard />
                          <strong>Forma de Pagamento:</strong> {activeProject.formaPagamento}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="items-container">
                  <div className="items-actions">
                      <button className="add-section-button" onClick={handleAddSection}>
                        <i className="fas fa-folder-plus"></i>
                        <span>Nova Seção</span>
                    </button>
                    <button className="add-item-button" onClick={handleAddItem}>
                        <i className="fas fa-plus-circle"></i>
                        <span>Novo Item</span>
                    </button>
                </div>
                
                    {getItemsBySections().length === 0 ? (
                      <div className="empty-items">
                    <i className="fas fa-clipboard-list"></i>
                        <h3>Nenhum item encontrado</h3>
                        <p>Adicione seções e itens para começar a montar seu orçamento</p>
                  </div>
                ) : (
                      <div className="sections-container">
                        {getItemsBySections().map(section => (
                          <div key={section.id} className="section">
                            <div className="section-header">
                              <h3>{section.titulo}</h3>
                              <div className="section-actions">
                                <button className="add-item-button" title="Adicionar item nesta seção" onClick={() => {
                                  setIsEditingItem(false);
                                  setEditingItem(null);
                                  setSelectedConfig(null);
                                  setNewItemData({
                                    descricao: '',
                                    unidade: '',
                                    quantidade: 1,
                                    materialUnitario: 0,
                                    valorCusto: 0,
                                    fatorLucro: 1.7,
                                    moUnitario: 0,
                                    observacao: '',
                                    secaoId: section.id
                                  });
                                  setAddItemModalOpen(true);
                                }}>
                                  <i className="fas fa-plus-circle"></i> Item
                                </button>
                                <button className="edit-button" title="Editar seção" onClick={() => handleEditSection(section)}>
                                  <i className="fas fa-edit"></i>
                    </button>
                                <button className="delete-button" title="Excluir seção" onClick={() => handleDeleteSection(section)}>
                                  <i className="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </div>
                
                            {section.itens.length === 0 ? (
                              <p className="no-items">Nenhum item nesta seção</p>
                            ) : (
                              <div className="table-responsive">
                    <table className="items-table">
                      <thead>
                        <tr>
                                      <th>Descrição</th>
                                      <th>Unidade</th>
                                      <th>Quantidade</th>
                                      <th>Material Unit.</th>
                                      <th>Valor Custo</th>
                                      <th>Fator Lucro</th>
                                      <th>MO Unit.</th>
                                      <th>Material Total</th>
                                      <th>MO Total</th>
                                      <th>Total</th>
                                      <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                                    {section.itens.map(item => {
                                      const materialTotal = Math.round(((item.materialUnitario || 0) * (item.quantidade || 0)) * 100) / 100;
                                      const moTotal = Math.round(((item.moUnitario || 0) * (item.quantidade || 0)) * 100) / 100;
                                      const custoTotal = Math.round(((item.valorCusto || 0) * (item.quantidade || 0)) * 100) / 100;
                                      const total = Math.round((materialTotal + moTotal + custoTotal) * 100) / 100;
                                      
                                      return (
                                        <tr key={item.id}>
                                          <td>{item.descricao}</td>
                                          <td>{item.unidade}</td>
                                          <td>{item.quantidade}</td>
                                          <td>{formatCurrency(item.materialUnitario)}</td>
                                          <td>{formatCurrency(item.valorCusto)}</td>
                                          <td>{(item.fatorLucro || 1.7).toFixed(2)}x</td>
                                          <td>{formatCurrency(item.moUnitario)}</td>
                                          <td>{formatCurrency(materialTotal)}</td>
                                          <td>{formatCurrency(moTotal)}</td>
                                          <td>{formatCurrency(total)}</td>
                                          <td>
                                            <div className="item-actions">
                                              <button className="edit-button" title="Editar item" onClick={() => handleEditItem(item)}>
                                                <i className="fas fa-edit"></i>
                                              </button>
                                              <button className="delete-button" title="Excluir item" onClick={() => handleDeleteItem(item)}>
                                                <i className="fas fa-trash-alt"></i>
                                              </button>
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                      </tbody>
                      <tfoot>
                                    <tr>
                                      <td colSpan="7" className="subtotal-label">Subtotal da Seção</td>
                                      <td>{formatCurrency(Math.round((section.itens.reduce((acc, item) => acc + ((item.materialUnitario || 0) * (item.quantidade || 0)), 0)) * 100) / 100)}</td>
                                      <td>{formatCurrency(Math.round((section.itens.reduce((acc, item) => acc + ((item.moUnitario || 0) * (item.quantidade || 0)), 0)) * 100) / 100)}</td>
                                      <td>{formatCurrency(Math.round((section.itens.reduce((acc, item) => {
                                        const materialTotal = (item.materialUnitario || 0) * (item.quantidade || 0);
                                        const moTotal = (item.moUnitario || 0) * (item.quantidade || 0);
                                        const custoTotal = (item.valorCusto || 0) * (item.quantidade || 0);
                                        return acc + materialTotal + moTotal + custoTotal;
                                      }, 0)) * 100) / 100)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
                        ))}
                        
                        <div className="totals-container">
                          <div className="total-row">
                            <span className="total-label">Material Total:</span>
                            <span className="total-value">{formatCurrency(Math.round((projectItems.reduce((acc, item) => {
                              if (item.tipo !== 'secao') {
                                return acc + ((item.materialUnitario || 0) * (item.quantidade || 0));
                              }
                              return acc;
                            }, 0)) * 100) / 100)}</span>
        </div>
                          <div className="total-row">
                            <span className="total-label">Mão de Obra Total:</span>
                            <span className="total-value">{formatCurrency(Math.round((projectItems.reduce((acc, item) => {
                              if (item.tipo !== 'secao') {
                                return acc + ((item.moUnitario || 0) * (item.quantidade || 0));
                              }
                              return acc;
                            }, 0)) * 100) / 100)}</span>
            </div>
                          <div className="total-row">
                            <span className="total-label">Custo Total:</span>
                            <span className="total-value">{formatCurrency(Math.round((projectItems.reduce((acc, item) => {
                              if (item.tipo !== 'secao') {
                                return acc + ((item.valorCusto || 0) * (item.quantidade || 0));
                              }
                              return acc;
                            }, 0)) * 100) / 100)}</span>
            </div>
                          <div className="total-row grand-total">
                            <span className="total-label">Valor Total do Orçamento:</span>
                            <span className="total-value">{formatCurrency(Math.round((projectItems.reduce((acc, item) => {
                              if (item.tipo !== 'secao') {
                                const materialTotal = (item.materialUnitario || 0) * (item.quantidade || 0);
                                const moTotal = (item.moUnitario || 0) * (item.quantidade || 0);
                                const custoTotal = (item.valorCusto || 0) * (item.quantidade || 0);
                                return acc + materialTotal + moTotal + custoTotal;
                              }
                              return acc;
                            }, 0)) * 100) / 100)}</span>
              </div>
              </div>
              </div>
                    )}
              </div>
              </div>
              ) : (
                <div className="no-project-selected">
                  <i className="fas fa-file-invoice"></i>
                  <h3>Nenhum orçamento encontrado</h3>
                  <p>Crie um novo orçamento para começar</p>
        </div>
      )}
        </div>
      )}
            </div>
            </div>

      {/* Modal para gerar PDF */}
      {pdfModalOpen && (
        <div className="modal-overlay">
          <div className="modal pdf-modal">
            <div className="modal-header">
              <h3>Informações para o PDF</h3>
              <button className="modal-close" onClick={() => setPdfModalOpen(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-description">
                Preencha as informações da empresa que constará no cabeçalho e rodapé do PDF gerado.
              </p>
              <div className="form-group">
                <label htmlFor="empresa-nome">Nome da Empresa</label>
                <input 
                  type="text" 
                  id="empresa-nome" 
                  value={pdfEmpresaInfo.nome}
                  onChange={(e) => setPdfEmpresaInfo({...pdfEmpresaInfo, nome: e.target.value})}
                  placeholder="Digite o nome da empresa"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="empresa-cnpj">CNPJ</label>
                <input 
                  type="text" 
                  id="empresa-cnpj" 
                  value={pdfEmpresaInfo.cnpj}
                  onChange={(e) => setPdfEmpresaInfo({...pdfEmpresaInfo, cnpj: e.target.value})}
                  placeholder="Digite o CNPJ da empresa"
                  required
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-button" onClick={() => setPdfModalOpen(false)}>Cancelar</button>
              <button className="save-button" onClick={handleGeneratePDFWithInfo}>
                <i className="fas fa-file-pdf"></i> Gerar PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {addSectionModalOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{isEditingSection ? 'Editar Seção' : 'Nova Seção'}</h3>
              <button className="modal-close" onClick={() => setAddSectionModalOpen(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="sectionTitle">Título da Seção</label>
                <input 
                  type="text" 
                  id="sectionTitle" 
                  value={sectionTitle}
                  onChange={(e) => setSectionTitle(e.target.value)}
                  placeholder="Digite o título da seção"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-button" onClick={() => setAddSectionModalOpen(false)}>Cancelar</button>
              <button className="save-button" onClick={handleSaveSection}>
                {loading ? <i className="fas fa-spinner fa-spin"></i> : (isEditingSection ? 'Atualizar' : 'Adicionar')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Adicionar Item */}
      {addItemModalOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{isEditingItem ? 'Editar Item' : 'Adicionar Item'}</h3>
              <button className="modal-close" onClick={handleCloseModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-content">
              <div className="config-selector">
                <h4><i className="fas fa-magic"></i> Pré-configurações disponíveis</h4>
                <select
                  className="config-select"
                  value={selectedConfig?.id || ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      // Converter para o tipo correto - as configurações podem ter IDs numéricos ou strings
                      const configId = isNaN(e.target.value) ? e.target.value : Number(e.target.value);

                      
                      // Verificar diretamente com a string e numericamente caso seja um ID numérico
                      const config = configuracoes.find(c => {
                        if (typeof c.id === 'number' && typeof configId === 'string') {
                          return c.id === Number(configId);
                        } else if (typeof c.id === 'string' && typeof configId === 'number') {
                          return c.id === String(configId);
                        } else {
                          return c.id === configId;
                        }
                      });
                      
                      if (config) {
                        handleConfigSelect(config);
                      } else {
                        console.error("Configuração não encontrada com ID:", configId);
                      }
                    } else {
                      // Opção vazia selecionada
                      setSelectedConfig(null);
                    }
                  }}
                >
                  <option value="">Selecione uma configuração (opcional)</option>
                  {configuracoes.map(config => (
                    <option key={config.id} value={config.id}>
                      {config.descricao} (MO: R$ {(config.maoDeObraCusto || 0).toFixed(2)} | Fator: {(config.fatorMultiplicacao || 1.7).toFixed(2)}x)
                    </option>
                  ))}
                </select>

                {selectedConfig ? (
                  <div className="config-info">
                    <p>Configuração selecionada: <strong>{selectedConfig.descricao}</strong></p>
                    <p>MO Custo: <strong>R$ {(selectedConfig.maoDeObraCusto || 0).toFixed(2)}</strong></p>
                    <p>Fator Multiplicação: <strong>{(selectedConfig.fatorMultiplicacao || 1.7).toFixed(2)}x</strong></p>
                    <p>MO Unitário calculado: <strong>R$ {((selectedConfig.maoDeObraCusto || 0) * (selectedConfig.fatorMultiplicacao || 1.7)).toFixed(2)}</strong></p>
                    <button 
                      type="button"
                      className="clear-config-button"
                      onClick={() => {
                        setSelectedConfig(null);
                        // Limpar apenas os campos que foram preenchidos pela configuração
                        setNewItemData({
                          ...newItemData,
                          descricao: '',
                          unidade: '',
                          materialUnitario: 0,
                          moUnitario: 0,
                          valorCusto: 0,
                          fatorLucro: 1.7,
                          observacao: ''
                        });
                      }}
                    >
                      <i className="fas fa-times"></i> Limpar configuração
                    </button>
                  </div>
                ) : (
                  <div className="config-info no-config">
                    <p>Selecione uma configuração para preencher os campos automaticamente ou continue para preenchimento manual.</p>
                  </div>
                )}
              </div>

              <div className="manual-fields">
                <div className="manual-fields-title">
                  <i className="fas fa-edit"></i> Edição Manual
                </div>
                <div className="form-group">
                  <label>Descrição <span className="required">*</span></label>
                  <input
                    type="text"
                    value={newItemData.descricao}
                    onChange={(e) => setNewItemData({ ...newItemData, descricao: e.target.value })}
                    placeholder="Descrição do item"
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group half">
                    <label>Unidade</label>
                    <div className="custom-unidade-dropdown">
                      <div className="unidade-input-container">
                        <input
                          type="text"
                          value={newItemData.unidade}
                          onChange={(e) => handleUnidadeFilter(e.target.value)}
                          onFocus={() => setShowUnidadesDropdown(true)}
                          onBlur={() => {
                            // Pequeno delay para permitir o clique no dropdown
                            setTimeout(() => {
                              setShowUnidadesDropdown(false);
                            }, 200);
                          }}
                          placeholder="Ex: m², m, un"
                          className="unidade-input"
                        />
                        <button 
                          type="button"
                          className="unidade-dropdown-toggle"
                          onClick={() => setShowUnidadesDropdown(!showUnidadesDropdown)}
                        >
                          <i className="fas fa-chevron-down"></i>
                        </button>
                      </div>
                      {showUnidadesDropdown && (
                        <div className="unidade-dropdown-menu">
                          {filteredUnidades.length > 0 ? (
                            filteredUnidades.map((unidade, index) => (
                              <div
                                key={index}
                                className="unidade-dropdown-item"
                                onClick={() => handleSelectUnidade(unidade)}
                              >
                                {unidade}
                              </div>
                            ))
                          ) : (
                            <div className="unidade-dropdown-empty">
                              Nenhuma unidade encontrada
                            </div>
                          )}
                        </div>
                      )}
                      <small className="input-help">Digite ou selecione uma unidade comum</small>
                    </div>
                  </div>
                  <div className="form-group half">
                    <label>Quantidade <span className="required">*</span></label>
                    <input
                      type="number"
                      value={newItemData.quantidade}
                      onChange={(e) => setNewItemData({ ...newItemData, quantidade: parseFloat(e.target.value) })}
                      placeholder="Quantidade"
                      min="0"
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group half">
                    <label>Valor de Custo (R$)</label>
                    <input
                      type="number"
                      value={newItemData.valorCusto}
                      onChange={(e) => {
                        const valorCusto = parseFloat(e.target.value) || 0;
                        const fatorLucro = newItemData.fatorLucro || 1.7;
                        const moUnitario = Math.round((valorCusto * fatorLucro) * 100) / 100;
                        setNewItemData({ 
                          ...newItemData, 
                          valorCusto: valorCusto,
                          moUnitario: moUnitario
                        });
                      }}
                      placeholder="0,00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="form-group half">
                    <label>Fator de Lucro</label>
                    <input
                      type="number"
                      value={newItemData.fatorLucro}
                      onChange={(e) => {
                        const fatorLucro = parseFloat(e.target.value) || 1.7;
                        const valorCusto = newItemData.valorCusto || 0;
                        const moUnitario = Math.round((valorCusto * fatorLucro) * 100) / 100;
                        setNewItemData({ 
                          ...newItemData, 
                          fatorLucro: fatorLucro,
                          moUnitario: moUnitario
                        });
                      }}
                      placeholder="1,7"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group half">
                    <label>Material Unitário (R$)</label>
                    <input
                      type="number" 
                      value={newItemData.materialUnitario}
                      onChange={(e) => setNewItemData({ ...newItemData, materialUnitario: parseFloat(e.target.value)})}
                      min="0"
                      placeholder="0,00"
                      step="0.01"
                    />
                  </div>
                  <div className="form-group half">
                    <label>MO Unitário (R$) <span className="required">*</span></label>
                    <div className="input-with-button">
                      <input
                        type="number"
                        value={newItemData.moUnitario}
                        onChange={(e) => {
                          const moUnitario = parseFloat(e.target.value) || 0;
                          const fatorLucro = newItemData.fatorLucro || 1.7;
                          // Cálculo reverso: custo = MO / fator de lucro
                          const valorCusto = fatorLucro > 0 ? Math.round((moUnitario / fatorLucro) * 100) / 100 : 0;
                          setNewItemData({ 
                            ...newItemData, 
                            moUnitario: moUnitario,
                            valorCusto: valorCusto
                          });
                        }}
                        placeholder="0,00"
                        min="0"
                        required
                        step="0.01"
                      />
                      <button 
                        title="Aplicar valor padrão"
                        onClick={handleApplyDefaultValues}
                      >
                        <i className="fas fa-magic"></i>
                      </button>
                    </div>
                    <small className="input-help">Digite diretamente ou será calculado: Custo × Fator de Lucro</small>
                  </div>
                </div>
                <div className="form-group">
                  <label>Adicionar à Seção</label>
                  {availableSections.length > 0 ? (
                    <select 
                      className="section-dropdown"
                      value={newItemData.secaoId}
                      onChange={(e) => {
                        const secaoId = e.target.value;
                        setNewItemData(prev => ({...prev, secaoId}));
                      }}
                    >
                      <option value="">-- Sem seção --</option>
                      {availableSections.map(section => (
                        <option key={section.id} value={section.id}>
                          {section.descricao}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="no-sections-message">
                      Nenhuma seção criada. <button 
                        type="button" 
                        className="create-section-inline"
                        onClick={() => {
                          handleCloseModal();
                          setTimeout(() => {
                            handleAddSection();
                          }, 300);
                        }}
                      >
                        <i className="fas fa-folder-plus"></i>
                        Criar Nova Seção
                      </button>
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>Observação (opcional)</label>
                  <textarea 
                    value={newItemData.observacao}
                    onChange={(e) => {
                      const texto = e.target.value;
                      // Remove quebras de linha e limita o texto a 450 caracteres
                      const textoSemQuebras = texto.replace(/[\r\n]+/g, ' ');
                      const textoLimitado = textoSemQuebras.slice(0, 450);
                      setNewItemData({...newItemData, observacao: textoLimitado});
                    }}
                    placeholder="Digite uma observação se necessário (máx. 450 caracteres)"
                    rows="3"
                    maxLength="450"
                  ></textarea>
                  <div className="character-count">
                    {newItemData.observacao ? newItemData.observacao.length : 0}/450 caracteres
                  </div>
                </div>
              </div>
              {error && <div className="error-message">{error}</div>}
            </div>
            <div className="modal-footer">
              <button className="cancel-button" onClick={handleCloseModal}>Cancelar</button>
              <button className="save-button" onClick={handleSaveItem}>{isEditingItem ? 'Salvar Alterações' : 'Adicionar Item'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmação para excluir seção */}
      {confirmDeleteSection && sectionToDelete && (
        <div className="modal-overlay">
          <div className="modal confirm-modal">
            <div className="modal-header warning">
              <h3><FiAlertTriangle /> Confirmar Exclusão</h3>
              <button className="modal-close" onClick={() => setConfirmDeleteSection(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p>
                <strong>Tem certeza que deseja excluir a seção "{sectionToDelete.titulo || sectionToDelete.descricao}"?</strong>
              </p>
              
              {deleteCount > 0 && (
                <div className="warning-text">
                  <p><FiAlertTriangle /> Esta ação também excluirá {deleteCount} item(ns) pertencentes a esta seção!</p>
                </div>
              )}
              
              <p>Esta ação não pode ser desfeita.</p>
            </div>
            <div className="modal-footer">
              <button className="cancel-button" onClick={() => setConfirmDeleteSection(false)}>Cancelar</button>
              <button className="delete-button" onClick={confirmSectionDeletion}>
                {loading ? <i className="fas fa-spinner fa-spin"></i> : 'Excluir Seção'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmação para excluir item */}
      {confirmDeleteItem && itemToDelete && (
        <div className="modal-overlay">
          <div className="modal confirm-modal">
            <div className="modal-header warning">
              <h3><FiAlertTriangle /> Confirmar Exclusão</h3>
              <button className="modal-close" onClick={() => setConfirmDeleteItem(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p>
                <strong>Tem certeza que deseja excluir o item "{itemToDelete.descricao}"?</strong>
              </p>
              <p>Esta ação não pode ser desfeita.</p>
            </div>
            <div className="modal-footer">
              <button className="cancel-button" onClick={() => setConfirmDeleteItem(false)}>Cancelar</button>
              <button className="delete-button" onClick={confirmItemDeletion}>
                {loading ? <i className="fas fa-spinner fa-spin"></i> : 'Excluir Item'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Adicionar o modal de confirmação */}
      {renderUnsavedChangesModal()}
      
      {/* Modal de confirmação */}
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={hideConfirmationModal}
        onConfirm={modalConfig.onConfirm}
        title={modalConfig.title}
        message={modalConfig.message}
        confirmText={modalConfig.confirmText}
        cancelText={modalConfig.cancelText}
        type={modalConfig.type}
      />
    </div>
  );
};

export default Dashboard; 